
import { useEffect, useRef, useState } from 'react';
import DataTable from '../../../components/data-table/DataTable';
import { getAccounts } from '../../../services/accountsService';
import SearchTable from '../../../components/search-table/SearchTable';
import { StyledBanks } from './Banks.styled';
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from '../../../helpers/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { AccountStructure } from '../../../models/accounts-structure';
import { Account, AccountSchema, AccountSearch } from '../../../models/account';
import { RootState } from '../../../redux/store';
import { Skeleton } from '@mui/material';
import { CanceledError } from 'axios';
import ListItemIcon from '@mui/material/ListItemIcon';
import MovingIcon from '@mui/icons-material/Moving';
import EditIcon from '@mui/icons-material/Edit';
import { setBanksSearch } from '../../../redux/features/browse';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from "@emotion/react";
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from '../../../services/errorHandling';
import { editLastBread, getLastAvailableLink } from '../../../components/seperator/Seperator.utils';
import { useGetAccountStructureQuery } from '../../../redux/api/accounts-structure.api';


interface BanksProps {
    searchFormData?: AccountSearch;
}

function Banks({ searchFormData }: BanksProps) {

    
    const { showBoundary } = useErrorBoundary();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch();

    const { data: banksStructure } = useGetAccountStructureQuery("בנק");
    
    // const banksStructure = useSelector((state: RootState) => state.structure.banks)
    const banksSearch = useSelector((state: RootState) => state.browse.banksSearch)

    const [data, setData] = useState<Account[]>([]);
    const [searchStructure, setSearchStructure] = useState<AccountStructure[]>();
    const [tableStructure, setTableStructure] = useState<AccountStructure[]>();
    const [sortKey, setSortKey] = useState<string>('id');
    const [sortMethod, setSortMethod] = useState<string>('DESC');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const options = [
        {
            label: "עריכה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><EditIcon fontSize="small" /></ListItemIcon>,
            href: ""
        },
        {
            label: "תנועות",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><MovingIcon fontSize="small" /></ListItemIcon>,
            href: "journal-entries"
        },
    
    ];

    const bankSearchForm = useForm<AccountSearch>({defaultValues:{
        "account_type": "בנק",
        ...banksSearch,
        ...searchFormData
    }});
    const bankSearchValues = bankSearchForm.watch();
    const bankSearchValuesRef = useRef(bankSearchValues);


    useEffect(() => {
        setSearchStructure(banksStructure?.filter(field => field.show_search))
        setTableStructure(banksStructure?.filter(field => field.show_browse))
    }, [banksStructure]);


    useEffect(() => {
        if (sortKey && sortMethod) {
            bankSearchForm.handleSubmit(onSearch)();
        }
    }, [sortKey, sortMethod]);

    useEffect(() => {
        if ((JSON.stringify(bankSearchValues) !== JSON.stringify(bankSearchValuesRef?.current)) && sortKey && sortMethod) {
            bankSearchForm.handleSubmit(onSearch)();
            bankSearchValuesRef.current = bankSearchValues;
        }
    }, [bankSearchValues]);



    function handleSortKeyChanged(value: string) {
        setSortKey(value);
    }

    function handleSortMethodChanged(value: string) {
        setSortMethod(value);
    }


    const onSearch: SubmitHandler<AccountSearch> = (data) => {
        dispatch(setBanksSearch(data));
        getData(data);
    }


    async function getData(data: AccountSearch) {

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        setIsLoading(true);

        try {
            const responseData = await getAccounts(deleteEmptyFieldsFromObject(data), sortKey || 'id', "10", sortMethod || 'ASC', ["customData", "entries.document.documentSettings"], abortControllerRef.current.signal);
            const banksParse = z.array(AccountSchema).parse(responseData);
            setData(banksParse || []);
            setIsLoading(false);
        } catch (error: unknown) {
            if (error instanceof CanceledError) {
                return;
            }
            handleApiErrors(error, showBoundary);
        }

    }


    function rowChosen<T extends Account>(row: T) {

        if(location.state?.breadcrumbData){
            navigate(getLastAvailableLink(location.state.breadcrumbData), {
                replace: true,
                state: {
                    ...location.state,
                    document_account: {...row, id: undefined, account_id: row.id},
                    car: null,
                    breadcrumbData: location.state.breadcrumbData.slice(0, -1)
                }
            });
        }
    }

    function createState(){
        return {
            ...location.state,
        }
    }

    function createBreadcrumbData(state: any){

        const breadcrumbData = [
            {
                name: "כרטסות",
            },
            {
                name: "בנקים",
                link: "/banks",
            },
        ];

        return [...(editLastBread(state?.breadcrumbData, `/banks`, state) ?? breadcrumbData)];
    }


    function editSearchStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/banks/structure/search', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה חיפוש" }]
            } 
        });
    }

    function editBrowseStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/banks/structure/browse', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה דפדוף" }]
            } 
        });
    }

    function reset() {

        const values = setAllValuesToNull(bankSearchForm.getValues());

        for (const key in values) {
            if (values.hasOwnProperty(key)) {
                bankSearchForm.setValue(key as keyof AccountSearch, values[key as keyof AccountSearch]);
            }
        }

        bankSearchForm.setValue("account_type", "בנק")
    }

    return (
        <StyledBanks>
            {searchStructure ?
                <SearchTable structure={searchStructure} setStructure={editSearchStructure} reset={reset} useForm={bankSearchForm} style={{ backgroundColor: "rgb(35, 48, 68)", padding: 5, marginButton: 20 }} /> :
                <Skeleton variant="rectangular" height={100} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
            {tableStructure ?
                <DataTable structure={tableStructure} setStructure={editBrowseStructure} sortKey={sortKey} setSortKey={handleSortKeyChanged} sortMethod={sortMethod} setSortMethod={handleSortMethodChanged} data={data} isLoading={isLoading} options={options} selectRow={location.state?.breadcrumbData ? true : false} onSelectRow={rowChosen}/> :
                <Skeleton variant="rectangular" height={150} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
        </StyledBanks>
    );
}

export default Banks;